import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getStoredPageSize } from "../../Utils/TableUtils/TableUtils"
import apiClient from "../../Utils/apiClient"
import HostnameLink from "../../components/HostnameLink/HostnameLink"
import PanelLayout from "../../Layout/PanelLayout"
import TableBox from "../../components/TableBox/TableBox"
import StatusTag from "../../components/StatusTag/StatusTag"
import { Button, DatePicker, Input, Modal, Segmented, Table, Tooltip, Space, message } from "antd"
import {
  ArrowLeftOutlined,
  CameraOutlined,
  DownloadOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import { AppConfig } from "../../config"
import {
  LIVE_ACTIVITY_FILTERS,
  CATEGORY_COLORS,
  activityLabel,
  activityTagLabel,
  activityAccent,
  activityPlatformLabel,
  visibleActivityTypes,
  formatDuration,
  formatIdle,
  formatPercent,
  formatCommDoing,
  matchesLiveFilter,
  isSuspiciousInput,
  suspiciousInputTitle,
} from "../../Utils/userActivity"
import { renderDate } from "../../Utils/utils"
import { useDeviceLive } from "../../Utils/useDeviceLive"
import EmptyState from "../../components/EmptyState/EmptyState"
import ActivityReport from "./ActivityReport"
import ActivityScreenshotSettings from "./ActivityScreenshotSettings"
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts"
import "./UserActivity.scss"

const shortUserName = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return "—"
  const base = raw.includes("\\") ? raw.split("\\").pop() : raw
  return base || raw
}

const userInitial = (value) => {
  const name = shortUserName(value)
  if (name === "—") return "?"
  return name.slice(0, 1).toUpperCase()
}

const CATEGORY_FALLBACK = "#64748b"
const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24]

function ActivityShot({ id, alt }) {
  const [src, setSrc] = useState("")
  useEffect(() => {
    if (!id) return undefined
    let url = ""
    let stop = false
    ;(async () => {
      try {
        const res = await fetch(
          `${AppConfig.API_URL}/sessions/screenshots/${id}/image`,
          { credentials: "include" }
        )
        if (!res.ok) return
        const blob = await res.blob()
        url = URL.createObjectURL(blob)
        if (!stop) setSrc(url)
      } catch (_) {
        /* ignore */
      }
    })()
    return () => {
      stop = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [id])
  if (!src) return <div className="activity-shot__ph" />
  return <img src={src} alt={alt || "Activity screenshot"} />
}

function ScreenshotStrip({ shots }) {
  const [open, setOpen] = useState(null)
  if (!shots?.length) return null
  const current = open
    ? shots.find((row) => String(row._id) === String(open))
    : null
  return (
    <div className="activity-shot">
      <div className="time-use__label">Random screenshots</div>
      <div className="activity-shot__row">
        {shots.map((shot) => (
          <button
            type="button"
            key={shot._id}
            className="activity-shot__card"
            onClick={() => setOpen(shot._id)}
          >
            <ActivityShot
              id={shot._id}
              alt={shot.windowTitle || shot.foregroundApp}
            />
            <span>
              {shot.capturedAt ? dayjs(shot.capturedAt).format("HH:mm") : ""}
              {shot.foregroundApp ? ` · ${shot.foregroundApp}` : ""}
            </span>
          </button>
        ))}
      </div>
      <Modal
        open={Boolean(current)}
        footer={null}
        width={840}
        onCancel={() => setOpen(null)}
        title={
          current
            ? `${dayjs(current.capturedAt).format("D MMM HH:mm")} · ${
                current.windowTitle || current.foregroundApp || "Screenshot"
              }`
            : "Screenshot"
        }
      >
        {current ? (
          <ActivityShot
            id={current._id}
            alt={current.windowTitle || "Activity screenshot"}
          />
        ) : null}
      </Modal>
    </div>
  )
}

function colorOf(category) {
  return CATEGORY_COLORS[category] || CATEGORY_FALLBACK
}

function clockLabel(value) {
  return value ? dayjs(value).format("HH:mm") : ""
}

function RankList({ title, items, totalSeconds, empty, onSelect, selectedKey }) {
  if (!items?.length) {
    return (
      <div className="time-use__chart">
        <div className="time-use__label">{title}</div>
        <div className="user-activity__muted">{empty}</div>
      </div>
    )
  }
  const max = items[0]?.seconds || 1
  return (
    <div className="time-use__chart">
      <div className="time-use__label">{title}</div>
      <div className="time-use__ranks">
        {items.map((item) => (
          <button
            type="button"
            className={`time-use__rank${
              selectedKey && selectedKey === item.key ? " is-active" : ""
            }${onSelect ? " is-clickable" : ""}`}
            key={item.key}
            onClick={onSelect ? () => onSelect(item.key) : undefined}
          >
            <Tooltip title={item.doing || item.label || item.key}>
              <div className="time-use__rank-name">{item.label || item.key}</div>
            </Tooltip>
            <div className="time-use__rank-track">
              <div
                className="time-use__rank-fill"
                style={{ width: `${Math.max(4, (item.seconds / max) * 100)}%` }}
              />
            </div>
            <div className="time-use__rank-meta">
              {formatDuration(item.seconds)}
              <span>{formatPercent(item.seconds, totalSeconds)}</span>
            </div>
            {item.details?.length ? (
              <div className="time-use__rank-doing">
                {item.details.slice(0, 4).map((detail) => (
                  <div className="time-use__rank-doing-row" key={detail.key}>
                    <span>{detail.label || detail.key}</span>
                    <em>{formatDuration(detail.seconds)}</em>
                  </div>
                ))}
                {item.details.length > 4 ? (
                  <div className="time-use__rank-doing-row">
                    <span>+{item.details.length - 4} more</span>
                  </div>
                ) : null}
              </div>
            ) : item.doing ? (
              <div className="time-use__rank-doing">{item.doing}</div>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

function DayTimeline({ timeline, category, appKey, nowPct }) {
  const spans = timeline.filter((span) => {
    if (category && span.category !== category) return false
    if (appKey && span.app !== appKey) return false
    return true
  })
  return (
    <div className="time-use__timeline">
      <div className="time-use__label">Timeline</div>
      <div className="time-use__day">
        {spans.map((span, index) => (
          <Tooltip
            key={`${span.start}-${index}`}
            title={`${clockLabel(span.start)}–${clockLabel(span.end)} · ${
              span.app || activityLabel(span.category)
            } · ${formatDuration(span.seconds)}${
              span.windowTitle ? ` · ${span.windowTitle}` : ""
            }`}
          >
            <div
              className="time-use__seg"
              style={{
                left: `${span.leftPct}%`,
                width: `${span.widthPct}%`,
                background: colorOf(span.category),
              }}
            />
          </Tooltip>
        ))}
        {nowPct != null ? (
          <div className="time-use__now" style={{ left: `${nowPct}%` }} />
        ) : null}
      </div>
      <div className="time-use__afk">
        {spans
          .filter((span) => span.idle || span.category === "idle")
          .map((span, index) => (
            <div
              key={`afk-${span.start}-${index}`}
              className="time-use__seg time-use__seg--afk"
              style={{
                left: `${span.leftPct}%`,
                width: `${span.widthPct}%`,
              }}
            />
          ))}
      </div>
      <div className="time-use__ticks">
        {HOUR_TICKS.map((hour) => (
          <span key={hour}>{String(hour).padStart(2, "0")}</span>
        ))}
      </div>
      <div className="time-use__track-caption">
        Colored blocks are the foreground app. Grey strip is AFK / idle.
        {nowPct != null ? " The line is now." : ""}
      </div>
    </div>
  )
}

function TimeUsePanel({
  data,
  loading,
  selectedHost,
  viewDate,
  onSelectHost,
  onBack,
  onDateChange,
  onOpenMap,
}) {
  const [category, setCategory] = useState("")
  const [appKey, setAppKey] = useState("")
  const [eventQuery, setEventQuery] = useState("")
  const categories = data?.categories || []
  const apps = data?.apps || []
  const titles = data?.titles || []
  const sites = data?.sites || []
  const devices = data?.devices || []
  const timeline = data?.timeline || []
  const events = data?.events || []
  const hours = data?.hours || []
  const screenshots = data?.screenshots || []
  const total = data?.totalSeconds || 0
  const date = viewDate || dayjs()
  const isToday = date.isSame(dayjs(), "day")
  const nowPct = isToday
    ? ((dayjs().hour() * 60 + dayjs().minute()) / (24 * 60)) * 100
    : null
  const hasFilter = Boolean(category || appKey)
  const hourKeys = [
    ...new Set(
      hours.flatMap((row) => (row.categories || []).map((item) => item.key))
    ),
  ]
  const hourRows = hours.map((row) => {
    const point = {
      hour: String(row.hour).padStart(2, "0"),
    }
    hourKeys.forEach((key) => {
      point[key] = 0
    })
    ;(row.categories || []).forEach((item) => {
      point[item.key] = item.seconds
    })
    return point
  })
  const filteredEvents = events.filter((item) => {
    if (category && item.category !== category) return false
    if (appKey && item.app !== appKey) return false
    if (eventQuery) {
      const hay = `${item.app || ""} ${item.windowTitle || ""} ${
        item.category || ""
      }`.toLowerCase()
      if (!hay.includes(eventQuery.toLowerCase())) return false
    }
    return true
  })

  const exportEvents = () => {
    const header = ["Start", "End", "App", "Category", "Window", "Seconds"]
    const lines = [
      header,
      ...filteredEvents.map((row) => [
        row.start ? dayjs(row.start).format("YYYY-MM-DD HH:mm:ss") : "",
        row.end ? dayjs(row.end).format("YYYY-MM-DD HH:mm:ss") : "",
        row.app || "",
        activityLabel(row.category),
        row.windowTitle || "",
        row.seconds || 0,
      ]),
    ]
    const csv = lines
      .map((cols) =>
        cols.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `time-use-${selectedHost || "fleet"}-${date.format(
      "YYYY-MM-DD"
    )}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (loading && !data) {
    return <div className="user-activity__muted">Loading time use…</div>
  }

  if (
    !selectedHost &&
    !categories.length &&
    !apps.length &&
    !devices.length &&
    !screenshots.length
  ) {
    return (
      <EmptyState
        title="No time-use data for this day"
        description="Hosted Windows agents record the foreground app, window title, and idle time about every 10 seconds."
      />
    )
  }

  if (!selectedHost) {
    return (
      <div className="time-use">
        <div className="user-activity__kpis">
          <div className="user-activity__kpi user-activity__kpi--static">
            <div>
              <span className="user-activity__kpi-label">Devices</span>
              <strong>{devices.length}</strong>
            </div>
          </div>
          <div className="user-activity__kpi user-activity__kpi--static">
            <div>
              <span className="user-activity__kpi-label">Focus</span>
              <strong>{formatDuration(data?.focusSeconds)}</strong>
            </div>
          </div>
          <div className="user-activity__kpi user-activity__kpi--static">
            <div>
              <span className="user-activity__kpi-label">Active</span>
              <strong>{formatDuration(data?.activeSeconds)}</strong>
            </div>
          </div>
          <div className="user-activity__kpi user-activity__kpi--static">
            <div>
              <span className="user-activity__kpi-label">AFK / idle</span>
              <strong>{formatDuration(data?.idleSeconds)}</strong>
            </div>
          </div>
          <div className="user-activity__kpi user-activity__kpi--static">
            <div>
              <span className="user-activity__kpi-label">Media</span>
              <strong>{formatDuration(data?.mediaSeconds)}</strong>
            </div>
          </div>
        </div>
        <div className="time-use__hint">
          Open a device for a 24-hour timeline on {date.format("D MMM YYYY")}.
          Click a row to see apps and what they were doing — window titles,
          sites, and documents — for that PC.
        </div>
        {apps.length || categories.length ? (
          <div className="time-use__charts">
            <RankList
              title="Applications — what they were doing"
              items={apps.slice(0, 8)}
              totalSeconds={total}
              empty="No app breakdown"
            />
            <RankList
              title="Top categories"
              items={categories}
              totalSeconds={total}
              empty="No categories"
            />
          </div>
        ) : null}
        <Table
          className="time-use__devices"
          rowKey="hostname"
          size="small"
          pagination={false}
          dataSource={devices}
          onRow={(record) => ({
            onClick: () => onSelectHost(record.hostname),
          })}
          columns={[
            {
              title: "Device",
              dataIndex: "hostname",
              render: (text) => <span className="users-name">{text}</span>,
            },
            { title: "User", dataIndex: "sessionUser", render: (v) => v || "—" },
            {
              title: "Focus",
              dataIndex: "focusSeconds",
              render: (value, row) => (
                <span>
                  {formatDuration(value)}
                  <div className="user-activity__signals">
                    {formatDuration(row.activeSeconds)} active ·{" "}
                    {formatDuration(row.idleSeconds)} AFK
                  </div>
                </span>
              ),
            },
            {
              title: "First / last",
              key: "span",
              width: 120,
              render: (_, row) =>
                row.firstActivity
                  ? `${clockLabel(row.firstActivity)}–${clockLabel(
                      row.lastActivity
                    )}`
                  : "—",
            },
            {
              title: "Top app",
              dataIndex: "topApp",
              render: (v) => v || "—",
            },
            {
              title: "Window",
              dataIndex: "topTitle",
              ellipsis: true,
              render: (v) => v || "—",
            },
            {
              title: "",
              key: "map",
              width: 72,
              render: (_, row) => (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    onOpenMap?.(row.hostname)
                  }}
                >
                  Map
                </Button>
              ),
            },
          ]}
        />
      </div>
    )
  }

  return (
    <div className="time-use">
      <div className="time-use__header">
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          All devices
        </Button>
        <div>
          <div className="time-use__host">{selectedHost}</div>
          <div className="user-activity__muted">
            Foreground app and window title, with AFK from keyboard/mouse idle
          </div>
        </div>
        <div className="time-use__nav">
          <Button
            onClick={() => onOpenMap?.(selectedHost)}
          >
            Map
          </Button>
          <Button
            icon={<LeftOutlined />}
            onClick={() => onDateChange?.(date.subtract(1, "day"))}
          />
          <Button
            icon={<RightOutlined />}
            disabled={isToday}
            onClick={() => onDateChange?.(date.add(1, "day"))}
          />
          <Button
            icon={<DownloadOutlined />}
            disabled={!filteredEvents.length}
            onClick={exportEvents}
          >
            Export
          </Button>
        </div>
      </div>

      <div className="user-activity__kpis">
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">Focus</span>
            <strong>{formatDuration(data?.focusSeconds)}</strong>
          </div>
        </div>
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">Focus share</span>
            <strong>{formatPercent(data?.focusSeconds, total)}</strong>
          </div>
        </div>
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">Active</span>
            <strong>{formatDuration(data?.activeSeconds)}</strong>
          </div>
        </div>
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">AFK / idle</span>
            <strong>{formatDuration(data?.idleSeconds)}</strong>
          </div>
        </div>
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">First / last</span>
            <strong>
              {clockLabel(data?.firstActivity) || "—"}–
              {clockLabel(data?.lastActivity) || "—"}
            </strong>
          </div>
        </div>
        <div className="user-activity__kpi user-activity__kpi--static">
          <div>
            <span className="user-activity__kpi-label">Longest stretch</span>
            <strong>
              {data?.longestSpan
                ? formatDuration(data.longestSpan.seconds)
                : "—"}
            </strong>
            {data?.longestSpan?.app ? (
              <span className="user-activity__signals">
                {data.longestSpan.app}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <ScreenshotStrip shots={screenshots} />

      {hasFilter ? (
        <div className="time-use__filters">
          <span>
            Showing
            {category ? ` ${activityLabel(category)}` : ""}
            {appKey ? ` · ${appKey}` : ""}
          </span>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setCategory("")
              setAppKey("")
            }}
          >
            Clear
          </Button>
        </div>
      ) : null}

      {timeline.length ? (
        <DayTimeline
          timeline={timeline}
          category={category}
          appKey={appKey}
          nowPct={nowPct}
        />
      ) : (
        <EmptyState
          title="No window-title timeline yet"
          description="That is typical for the older Python agent. Hosted Windows agents fill this in."
        />
      )}

      {categories.length ? (
        <div className="time-use__legend">
          {categories.map((entry) => (
            <button
              type="button"
              key={entry.key}
              className={`time-use__legend-item${
                category === entry.key ? " is-active" : ""
              }`}
              onClick={() =>
                setCategory((current) => (current === entry.key ? "" : entry.key))
              }
            >
              <span
                className="time-use__swatch"
                style={{ background: colorOf(entry.key) }}
              />
              {entry.label}: {formatDuration(entry.seconds)} (
              {formatPercent(entry.seconds, total)})
            </button>
          ))}
        </div>
      ) : null}

      {hourKeys.length ? (
        <div className="time-use__chart">
          <div className="time-use__label">Activity by hour</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourRows} margin={{ left: 8, right: 8 }}>
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(value) => `${Math.round(value / 60)}m`}
                width={36}
                tick={{ fontSize: 11 }}
              />
              <RechartTooltip
                formatter={(value, name) => [
                  formatDuration(value),
                  activityLabel(name),
                ]}
              />
              {hourKeys.map((key) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="day"
                  fill={colorOf(key)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <div className="time-use__charts">
        <RankList
          title="Applications — what they were doing"
          items={apps}
          totalSeconds={total}
          empty="No app breakdown"
          selectedKey={appKey}
          onSelect={(key) =>
            setAppKey((current) => (current === key ? "" : key))
          }
        />
        <RankList
          title="Top window titles"
          items={titles}
          totalSeconds={total}
          empty="No window titles yet"
        />
        <RankList
          title="Top sites / pages"
          items={sites}
          totalSeconds={total}
          empty="No browser pages in window titles"
        />
      </div>

      {events.length ? (
        <div className="time-use__chart">
          <div className="time-use__events-head">
            <div className="time-use__label">Events</div>
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Filter events"
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
              style={{ maxWidth: 240 }}
            />
          </div>
          <Table
            rowKey={(row) => `${row.start}-${row.app}-${row.windowTitle}`}
            size="small"
            pagination={{ pageSize: 12, showSizeChanger: false }}
            dataSource={filteredEvents}
            columns={[
              {
                title: "When",
                dataIndex: "start",
                width: 150,
                render: (value, row) =>
                  `${clockLabel(value)}–${clockLabel(row.end)}`,
              },
              {
                title: "App",
                dataIndex: "app",
                width: 160,
                render: (value, row) => (
                  <span>
                    {value || activityLabel(row.category)}
                    <div className="user-activity__signals">
                      {activityLabel(row.category)}
                    </div>
                  </span>
                ),
              },
              {
                title: "Window title",
                dataIndex: "windowTitle",
                ellipsis: true,
                render: (value) => value || "—",
              },
              {
                title: "Duration",
                dataIndex: "seconds",
                width: 110,
                render: formatDuration,
              },
            ]}
          />
        </div>
      ) : null}
    </div>
  )
}

const UserActivity = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const livePage = "User Activity Live"
  const historyPage = "User Activity"
  const [mode, setMode] = useState("live")
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({})
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [activity, setActivity] = useState("all")
  const [timeUseDate, setTimeUseDate] = useState(() => dayjs())
  const [historyDate, setHistoryDate] = useState(() => dayjs())
  const [timeUse, setTimeUse] = useState(null)
  const [activityReport, setActivityReport] = useState(null)
  const [reportPeriod, setReportPeriod] = useState("week")
  const [reportDate, setReportDate] = useState(() => dayjs())
  const [reportFrom, setReportFrom] = useState(() => dayjs().startOf("isoWeek"))
  const [reportTo, setReportTo] = useState(() => dayjs().endOf("isoWeek"))
  const [reportOpts, setReportOpts] = useState({
    excludeWeekends: true,
    excludeHolidays: true,
    group: "",
    location: "",
    targetHoursPerDay: 8,
  })
  const [selectedHost, setSelectedHost] = useState("")
  const [nowTick, setNowTick] = useState(0)
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(livePage) || 10,
    total: 0,
  })
  const [shotSettingsOpen, setShotSettingsOpen] = useState(false)
  const [shotEnabled, setShotEnabled] = useState(true)
  const [shotExcludeCount, setShotExcludeCount] = useState(0)

  useEffect(() => {
    apiClient("settings/activity-screenshots", "GET")
      .then((res) => {
        if (!res?.result) return
        setShotEnabled(res.result.enabled !== false)
        setShotExcludeCount(
          Array.isArray(res.result.excludedHostnames)
            ? res.result.excludedHostnames.length
            : 0
        )
      })
      .catch(() => {})
  }, [])

  const onShotSettingsClose = (next) => {
    setShotSettingsOpen(false)
    if (!next) return
    setShotEnabled(next.enabled !== false)
    setShotExcludeCount(
      Array.isArray(next.excludedHostnames) ? next.excludedHostnames.length : 0
    )
  }

  useEffect(() => {
    const host = String(searchParams.get("host") || "").trim()
    if (!host) return
    setSelectedHost(host)
    setSearchInput(host)
    setMode("timeuse")
  }, [searchParams])

  const fetchLive = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customActivity = activity,
    customSearch = search,
    silent = false
  ) => {
    if (!silent) setLoading(true)
    try {
      const res = await apiClient("sessions/live", "GET", null, {
        params: {
          page: customPage,
          pageSize: customPageSize,
          activity: customActivity,
          search: customSearch,
        },
      })
      if (res?.data) {
        setRows(res.data)
        setPaginationData(res.pageData)
        setSummary(res.summary || {})
      }
    } catch (err) {
      console.error("Error fetching live activity", err)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const fetchHistory = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customActivity = activity,
    customSearch = search
  ) => {
    setLoading(true)
    try {
      const range = [
        historyDate.startOf("day").toISOString(),
        historyDate.endOf("day").toISOString(),
      ]
      const res = await apiClient("sessions", "GET", null, {
        params: {
          page: customPage,
          pageSize: customPageSize,
          activity: customActivity,
          search: customSearch,
          dateRange: range,
        },
      })
      if (res?.data) {
        setRows(res.data)
        setPaginationData(res.pageData)
        setSummary({})
      }
    } catch (err) {
      console.error("Error fetching session history", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchTimeUse = async (
    customSearch = search,
    customDate = timeUseDate,
    customHost = selectedHost
  ) => {
    setLoading(true)
    try {
      const res = await apiClient("sessions/time-use", "GET", null, {
        params: {
          search: customHost ? undefined : customSearch,
          hostname: customHost || undefined,
          date: customDate?.startOf("day").toISOString(),
        },
      })
      if (res) setTimeUse(res)
    } catch (err) {
      console.error("Error fetching time use", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchReport = async (
    customSearch = search,
    customPeriod = reportPeriod,
    customDate = reportDate,
    customFrom = reportFrom,
    customTo = reportTo,
    customOpts = reportOpts
  ) => {
    setLoading(true)
    try {
      const kind = customPeriod
      let from = customFrom
      let to = customTo
      if (kind === "day") {
        from = (customDate || dayjs()).startOf("day")
        to = (customDate || dayjs()).endOf("day")
      } else if (kind === "week") {
        from = (customDate || dayjs()).startOf("isoWeek")
        to = (customDate || dayjs()).endOf("isoWeek")
      } else if (kind === "month") {
        from = (customFrom || (customDate || dayjs()).startOf("month")).startOf(
          "day"
        )
        to = (customTo || (customDate || dayjs()).endOf("month")).endOf("day")
      } else {
        from = (customFrom || dayjs().startOf("month")).startOf("day")
        to = (customTo || dayjs()).endOf("day")
      }
      const res = await apiClient("sessions/report", "GET", null, {
        params: {
          search: customSearch || undefined,
          period: kind === "custom" ? "custom" : kind,
          date: (customDate || dayjs()).startOf("day").toISOString(),
          from: from ? from.startOf("day").toISOString() : undefined,
          to: to ? to.endOf("day").toISOString() : undefined,
          excludeWeekends: customOpts.excludeWeekends !== false,
          excludeHolidays: customOpts.excludeHolidays !== false,
          targetHoursPerDay: customOpts.targetHoursPerDay || 8,
          group: customOpts.group || undefined,
          location: customOpts.location || undefined,
        },
      })
      if (res) setActivityReport(res)
    } catch (err) {
      console.error("Error fetching activity report", err)
    } finally {
      setLoading(false)
    }
  }

  const load = (
    page = paginationData.page,
    pageSize = paginationData.pageSize,
    nextActivity = activity,
    nextSearch = search,
    nextMode = mode
  ) => {
    if (nextMode === "timeuse") {
      return fetchTimeUse(nextSearch, timeUseDate, selectedHost)
    }
    if (nextMode === "report") {
      return fetchReport(
        nextSearch,
        reportPeriod,
        reportDate,
        reportFrom,
        reportTo,
        reportOpts
      )
    }
    if (nextMode === "live") {
      return fetchLive(page, pageSize, nextActivity, nextSearch)
    }
    return fetchHistory(page, pageSize, nextActivity, nextSearch)
  }

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPaginationData((prev) => ({ ...prev, page: 1 }))
    load(1, paginationData.pageSize, activity, search, mode)
  }, [mode, activity, search, timeUseDate, selectedHost, historyDate, reportPeriod, reportDate, reportFrom, reportTo, reportOpts])

  useEffect(() => {
    if (mode !== "live") return undefined
    const tick = setInterval(() => setNowTick((value) => value + 1), 15000)
    const timer = setInterval(() => {
      fetchLive(
        paginationData.page,
        paginationData.pageSize,
        activity,
        search,
        true
      )
    }, 8000)
    return () => {
      clearInterval(tick)
      clearInterval(timer)
    }
  }, [mode, activity, search, paginationData.page, paginationData.pageSize])

  useDeviceLive((event) => {
    if (mode !== "live") return
    const patched = {
      hostname: event.hostname,
      serialNumber: event.serialNumber,
      sessionUser: event.sessionUser || event.user,
      deviceStatus: event.deviceStatus,
      userStatus: event.userStatus,
      idleSeconds: event.idleSeconds,
      appName: event.appName,
      windowTitle: event.windowTitle,
      activityLabel: event.activityLabel,
      activityDetail: event.activityDetail,
      activitySubject: event.activitySubject,
      activityStartedAt: event.activityStartedAt,
      primaryActivity: event.primaryActivity,
      activityTypes: event.activityTypes || [],
      lastReportReceivedTime: event.lastReportReceivedTime,
      lastSeen: event.lastReportReceivedTime,
      inputIntegrityStatus: event.inputIntegrityStatus,
      inputIntegrity: event.inputIntegrity || null,
      lastInputIntegrityEnforceAt: event.lastInputIntegrityEnforceAt || null,
    }
    if (search) {
      const hay = [
        patched.hostname,
        patched.sessionUser,
        patched.appName,
        patched.windowTitle,
        patched.activityDetail,
        patched.activitySubject,
      ]
        .join(" ")
        .toLowerCase()
      if (!hay.includes(search.toLowerCase())) {
        setRows((current) =>
          current.filter((row) => row.hostname !== patched.hostname)
        )
        return
      }
    }
    if (!matchesLiveFilter(patched, activity)) {
      setRows((current) =>
        current.filter((row) => row.hostname !== patched.hostname)
      )
      return
    }
    setRows((current) => {
      const idx = current.findIndex(
        (row) =>
          row.hostname === patched.hostname ||
          (patched.serialNumber && row.serialNumber === patched.serialNumber)
      )
      if (idx < 0) return [patched, ...current]
      const next = [...current]
      next[idx] = { ...current[idx], ...patched }
      return next
    })
  })

  const openTimeUse = (hostname, date) => {
    if (date) setTimeUseDate(dayjs(date))
    setSelectedHost(hostname || "")
    setMode("timeuse")
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    load(current, pageSize)
  }

  const renderActivity = (record) => {
    const types = visibleActivityTypes(
      record.activityTypes,
      record.primaryActivity
    )
    if (!types.length && !isSuspiciousInput(record)) {
      return <span className="user-activity__muted">—</span>
    }
    const platform = activityPlatformLabel(record)
    return (
      <div className="user-activity__tags">
        {isSuspiciousInput(record) ? (
          <Tooltip title={suspiciousInputTitle(record)}>
            <span
              className="user-activity__chip is-alert"
              style={{
                "--act-tone": activityAccent("suspicious"),
                "--act-soft": `${activityAccent("suspicious")}22`,
              }}
            >
              <i />
              Suspicious input
            </span>
          </Tooltip>
        ) : null}
        {record.lastInputIntegrityEnforceAt ? (
          <Tooltip title="Screenshots capturing · IT alerted · no lock · no user notice">
            <span
              className="user-activity__chip is-alert"
              style={{
                "--act-tone": "#9f1239",
                "--act-soft": "#9f123922",
              }}
            >
              <i />
              IT alerted
            </span>
          </Tooltip>
        ) : null}
        {types.map((type) => (
          <span
            key={type}
            className="user-activity__chip"
            style={{
              "--act-tone": activityAccent(type),
              "--act-soft": `${activityAccent(type)}18`,
            }}
            title={activityLabel(type)}
          >
            <i />
            {activityTagLabel(type, record)}
          </span>
        ))}
        {platform ? (
          <span className="user-activity__chip is-soft">{platform}</span>
        ) : null}
      </div>
    )
  }

  const renderDoing = (record) => {
    void nowTick
    const primary = record.primaryActivity
    const comm = formatCommDoing(record)
    const app = record.appName || ""
    const title = record.windowTitle || ""
    const detail = record.activityDetail || ""
    const main =
      comm?.main ||
      detail ||
      title ||
      app ||
      activityLabel(record.primaryActivity)
    const extras = comm ? [...comm.extras] : []
    if (!comm) {
      if (
        title &&
        !String(main).toLowerCase().includes(title.toLowerCase().slice(0, 28))
      ) {
        extras.push(title)
      }
      if (
        app &&
        !String(main).toLowerCase().includes(app.toLowerCase()) &&
        !extras.some((item) => item.toLowerCase().includes(app.toLowerCase()))
      ) {
        extras.push(app)
      }
    }
    if (
      ["rdp", "vdi"].includes(record.sessionKind) &&
      record.clientName
    ) {
      extras.push(`${record.sessionKind.toUpperCase()} · ${record.clientName}`)
    }
    if (isSuspiciousInput(record)) {
      extras.unshift("Suspicious input · low diversity")
    }
    return (
      <Tooltip
        title={
          [
            isSuspiciousInput(record) ? suspiciousInputTitle(record) : "",
            comm?.main,
            title,
            app,
            detail,
            ...(record.activitySignals || []),
          ]
            .filter(Boolean)
            .join(" · ") || main
        }
      >
        <div className="user-activity__doing">
          <span
            className="user-activity__doing-dot"
            style={{
              background: isSuspiciousInput(record)
                ? activityAccent("suspicious")
                : activityAccent(primary),
            }}
            aria-hidden
          />
          <div>
            <div className="user-activity__detail">{main}</div>
            {extras.length ? (
              <div className="user-activity__signals">{extras.join(" · ")}</div>
            ) : null}
          </div>
        </div>
      </Tooltip>
    )
  }

  const liveColumns = [
    {
      title: "User",
      dataIndex: "sessionUser",
      key: "sessionUser",
      render: (user) => (
        <div className="user-activity__person">
          <span
            className="user-activity__avatar"
            style={{ background: activityAccent("app") }}
            aria-hidden
          >
            {userInitial(user)}
          </span>
          <div>
            <span className="users-name">{shortUserName(user)}</span>
            {user && String(user).includes("\\") ? (
              <span className="user-activity__signals">{user}</span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      render: (text, record) => (
        <HostnameLink record={record}>{text || "—"}</HostnameLink>
      ),
    },
    {
      title: "Presence",
      key: "presence",
      width: 120,
      render: (_, record) => (
        <StatusTag status={record.userStatus || record.deviceStatus || "Offline"} />
      ),
    },
    {
      title: "Activity",
      key: "activity",
      width: 180,
      render: (_, record) => renderActivity(record),
    },
    {
      title: "What they are doing",
      key: "detail",
      render: (_, record) => renderDoing(record),
    },
    {
      title: "Screen",
      key: "screenshot",
      width: 88,
      render: (_, record) =>
        record.lastScreenshotId ? (
          <button
            type="button"
            className="activity-shot__live"
            onClick={(event) => {
              event.stopPropagation()
              openTimeUse(record.hostname)
            }}
          >
            <ActivityShot id={record.lastScreenshotId} alt="Latest screenshot" />
          </button>
        ) : (
          <span className="user-activity__muted">—</span>
        ),
    },
    {
      title: "Idle",
      dataIndex: "idleSeconds",
      key: "idleSeconds",
      width: 120,
      render: (seconds) => formatIdle(seconds),
    },
    {
      title: "Last seen",
      dataIndex: "lastSeen",
      key: "lastSeen",
      width: 170,
      render: (value) => renderDate(value) || "—",
    },
    {
      title: "",
      key: "day",
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              openTimeUse(record.hostname)
            }}
          >
            Day
          </Button>
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              navigate(
                `/device-tracker?host=${encodeURIComponent(record.hostname || "")}`
              )
            }}
          >
            Map
          </Button>
        </Space>
      ),
    },
  ]

  const historyColumns = [
    ...liveColumns.filter(
      (col) =>
        col.key !== "idleSeconds" &&
        col.key !== "presence" &&
        col.key !== "day"
    ),
    {
      title: "Session",
      dataIndex: "activeSeconds",
      key: "activeSeconds",
      render: (value) => formatDuration(value),
    },
    {
      title: "Active",
      dataIndex: "cumulativeActiveSeconds",
      key: "cumulativeActiveSeconds",
      render: (value) => formatDuration(value),
    },
    {
      title: "",
      key: "day",
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              openTimeUse(record.hostname, historyDate)
            }}
          >
            Day
          </Button>
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              navigate(
                `/device-tracker?host=${encodeURIComponent(record.hostname || "")}`
              )
            }}
          >
            Map
          </Button>
        </Space>
      ),
    },
  ]

  const exportOptions = {
    endpoint: "filter/sessions",
    method: "POST",
    params: {
      activity: activity === "all" ? "" : activity,
      search,
      dateRange: [
        historyDate.startOf("day").toISOString(),
        historyDate.endOf("day").toISOString(),
      ],
    },
    format: (data) =>
      (data || []).map((session) => ({
        user: session.sessionUser || "N/A",
        hostname: session.hostname || "N/A",
        activity: activityLabel(session.primaryActivity),
        app: session.appName || session.foregroundApp || "",
        window: session.windowTitle || "",
        detail: session.activityDetail || session.windowTitle || "",
        sessionKind: session.sessionKind || "",
        loginTime: renderDate(session.loginTime),
        lastSeen: renderDate(session.lastSeen),
        sessionDuration: formatDuration(session.activeSeconds),
        activityDuration: formatDuration(session.cumulativeActiveSeconds),
      })),
    header: [
      "User",
      "Hostname",
      "Activity",
      "App",
      "Window",
      "Detail",
      "Session kind",
      "Login time",
      "Last seen",
      "Session",
      "Active",
    ],
    btnTitle: "Export",
    fileName: "User Activity",
    pdfWithout: [],
  }

  const liveTotal = Number(summary.all) || 0
  const liveMix = LIVE_ACTIVITY_FILTERS.filter((item) => item.key !== "all")
    .map((item) => ({
      key: item.key,
      name: item.label,
      value: Number(summary[item.key]) || 0,
      color: activityAccent(item.key),
    }))
    .filter((row) => row.value > 0)
  const liveSubtitle =
    mode === "live"
      ? `${liveTotal} online now · filter by activity or search a user, tab, or app`
      : mode === "history"
        ? "Sessions for the selected day — filter by activity type."
        : mode === "timeuse"
          ? "Day timeline, focus vs idle, and app/site ranks."
          : "Focus and work-hour coverage across the fleet."

  return (
    <PanelLayout
      title="User activity"
      subtitle={liveSubtitle}
      actions={
        <>
          <Input
            className="users-search"
            prefix={<SearchOutlined />}
            allowClear
            placeholder={
              mode === "timeuse"
                ? selectedHost
                  ? selectedHost
                  : "Find a device or user…"
                : mode === "report"
                ? "Find a user or device…"
                : "Find user, YouTube, Chrome, app…"
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            icon={<CameraOutlined />}
            onClick={() => setShotSettingsOpen(true)}
          >
            Screenshots
            {shotEnabled === false
              ? " · Off"
              : shotExcludeCount
              ? ` · ${shotExcludeCount} excluded`
              : ""}
          </Button>
          {mode === "timeuse" || mode === "history" ? (
            <DatePicker
              value={mode === "timeuse" ? timeUseDate : historyDate}
              onChange={(value) => {
                const next = value || dayjs()
                if (mode === "timeuse") setTimeUseDate(next)
                else setHistoryDate(next)
              }}
              allowClear={false}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          ) : null}
        </>
      }
      exportable={mode === "history"}
      exportContent={exportOptions}
    >
      <ActivityScreenshotSettings
        open={shotSettingsOpen}
        onClose={onShotSettingsClose}
      />
      <div className="user-activity">
        <Segmented
          className="user-activity__modes"
          value={mode}
          onChange={(value) => {
            setActivity("all")
            setSelectedHost("")
            setMode(value)
          }}
          options={[
            { label: "Live now", value: "live" },
            {
              label: historyDate.isSame(dayjs(), "day")
                ? "Today"
                : historyDate.format("D MMM"),
              value: "history",
            },
            { label: "Time use", value: "timeuse" },
            { label: "Work hours", value: "report" },
          ]}
        />

        {mode === "report" ? (
          <ActivityReport
            data={activityReport}
            loading={loading}
            period={reportPeriod}
            date={reportDate}
            rangeFrom={reportFrom}
            rangeTo={reportTo}
            opts={reportOpts}
            onPeriodChange={(value) => {
              setReportPeriod(value)
              if (value === "month") {
                const base = reportDate || dayjs()
                setReportFrom(base.startOf("month"))
                setReportTo(
                  base.endOf("month").isAfter(dayjs())
                    ? dayjs()
                    : base.endOf("month")
                )
              } else if (value === "custom" && (!reportFrom || !reportTo)) {
                setReportFrom(dayjs().startOf("month"))
                setReportTo(dayjs())
              } else if (value === "day") {
                const base = reportDate || dayjs()
                setReportFrom(base.startOf("day"))
                setReportTo(base.endOf("day"))
              } else if (value === "week") {
                const base = reportDate || dayjs()
                setReportFrom(base.startOf("isoWeek"))
                setReportTo(base.endOf("isoWeek"))
              }
            }}
            onDateChange={(value) => {
              const next = value || dayjs()
              setReportDate(next)
              if (reportPeriod === "day") {
                setReportFrom(next.startOf("day"))
                setReportTo(next.endOf("day"))
              } else if (reportPeriod === "week") {
                setReportFrom(next.startOf("isoWeek"))
                setReportTo(next.endOf("isoWeek"))
              } else if (reportPeriod === "month") {
                setReportFrom(next.startOf("month"))
                setReportTo(
                  next.endOf("month").isAfter(dayjs())
                    ? dayjs()
                    : next.endOf("month")
                )
              }
            }}
            onRangeChange={(from, to) => {
              setReportFrom(from)
              setReportTo(to)
              if (from) setReportDate(from)
            }}
            onOptsChange={(patch) =>
              setReportOpts((prev) => ({ ...prev, ...patch }))
            }
            onOpenDay={(hostname, date) => openTimeUse(hostname, date)}
          />
        ) : mode === "timeuse" ? (
          <TimeUsePanel
            key={`${selectedHost || "fleet"}-${timeUseDate.format("YYYY-MM-DD")}`}
            data={timeUse}
            loading={loading}
            selectedHost={selectedHost}
            viewDate={timeUseDate}
            onSelectHost={setSelectedHost}
            onBack={() => setSelectedHost("")}
            onDateChange={setTimeUseDate}
            onOpenMap={(hostname) =>
              navigate(
                `/device-tracker?host=${encodeURIComponent(hostname || "")}`
              )
            }
          />
        ) : (
          <>
            {mode === "live" ? (
              <div className="user-activity__live-board">
                <div className="user-activity__mix">
                  <div className="user-activity__mix-title">Live mix</div>
                  <div className="user-activity__mix-hint">
                    Click a slice or KPI to filter
                  </div>
                  {liveMix.length ? (
                    <div className="user-activity__mix-chart">
                      <ResponsiveContainer width="100%" height={148}>
                        <PieChart>
                          <Pie
                            data={liveMix}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={44}
                            outerRadius={62}
                            paddingAngle={2}
                            stroke="#fff"
                            strokeWidth={2}
                          >
                            {liveMix.map((entry) => (
                              <Cell
                                key={entry.key}
                                fill={entry.color}
                                cursor="pointer"
                                onClick={() =>
                                  setActivity((current) =>
                                    current === entry.key ? "all" : entry.key
                                  )
                                }
                              />
                            ))}
                          </Pie>
                          <RechartTooltip
                            formatter={(value, name) => [`${value} people`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="user-activity__mix-center">
                        <strong>{liveTotal}</strong>
                        <span>Live</span>
                      </div>
                    </div>
                  ) : (
                    <div className="user-activity__empty">No live sessions</div>
                  )}
                </div>
                <div className="user-activity__kpis user-activity__kpis--live">
                  {LIVE_ACTIVITY_FILTERS.map((item) => {
                    const count =
                      item.key === "all"
                        ? summary.all ?? 0
                        : summary[item.key] ?? 0
                    const tone = activityAccent(item.key)
                    const pct =
                      item.key === "all" || !liveTotal
                        ? null
                        : Math.round((Number(count) / liveTotal) * 100)
                    const empty = item.key !== "all" && !Number(count)
                    return (
                      <button
                        type="button"
                        key={item.key}
                        className={`user-activity__kpi${
                          activity === item.key ? " is-active" : ""
                        }${empty ? " is-empty" : ""}`}
                        style={{ "--act-tone": tone, "--act-soft": `${tone}14` }}
                        onClick={() => setActivity(item.key)}
                      >
                        <div>
                          <span className="user-activity__kpi-label">
                            {item.label}
                          </span>
                          <strong>{count}</strong>
                          {pct != null ? (
                            <span className="user-activity__kpi-meta">{pct}% of live</span>
                          ) : (
                            <span className="user-activity__kpi-meta">All online</span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="user-activity__filter-row">
                {LIVE_ACTIVITY_FILTERS.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    className={`user-activity__pill${
                      activity === item.key ? " is-active" : ""
                    }`}
                    style={{ "--act-tone": activityAccent(item.key) }}
                    onClick={() => setActivity(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            <div className="user-activity__toolbar">
              <span>
                {paginationData.total ?? rows.length}{" "}
                {mode === "live" ? "people" : "sessions"}
                {activity !== "all"
                  ? ` · ${
                      LIVE_ACTIVITY_FILTERS.find((item) => item.key === activity)
                        ?.label || activity
                    }`
                  : ""}
                {search ? ` · “${search}”` : ""}
              </span>
              {activity !== "all" ? (
                <Button size="small" onClick={() => setActivity("all")}>
                  Clear filter
                </Button>
              ) : null}
            </div>

            {rows.length || loading ? (
              <TableBox
                loading={loading}
                page={mode === "live" ? livePage : historyPage}
                columns={mode === "live" ? liveColumns : historyColumns}
                data={rows}
                pagination={{
                  current: paginationData.page,
                  pageSize: paginationData.pageSize,
                  total: paginationData.total,
                  showSizeChanger: true,
                }}
                onChange={handleTableChange}
                title={null}
                onRow={(record) => ({
                  onClick: () =>
                    openTimeUse(
                      record.hostname,
                      mode === "live" ? undefined : historyDate
                    ),
                  style: { cursor: "pointer" },
                })}
              />
            ) : (
              <EmptyState
                title={activity !== "all" ? "No matches for this filter" : "No activity"}
                description="Try another activity filter or clear search."
              />
            )}
          </>
        )}
      </div>
    </PanelLayout>
  )
}

export default UserActivity
